import type { DESTROY } from '@app/application/user-management/constants'

export const userLists = {
  STAFF: 'staff',
  EXTERNAL: 'external',
} as const

export type UserList = typeof userLists[keyof typeof userLists]

export const userStatuses = {
  ACTIVE: 'active',
  UNCONFIRMED: 'unconfirmed',
  DEACTIVATED: 'deactivated',
} as const

export type UserStatus = typeof userStatuses[keyof typeof userStatuses]

export const userRoles = {
  ADMIN: 'admin',
  STAFF: 'staff',
  CONTRIBUTOR: 'contributor',
  PULSE: 'pulse',
} as const

export type UserRole = typeof userRoles[keyof typeof userRoles]

// Mirrors the roles UsersListQuery filters on for each list, so a change here
// does not reach the server. A role of the other list narrows nothing.
export const userListRoles: Record<UserList, readonly UserRole[]> = {
  [userLists.STAFF]: [userRoles.ADMIN, userRoles.STAFF],
  [userLists.EXTERNAL]: [userRoles.CONTRIBUTOR, userRoles.PULSE],
}

// The values of UserManagement::Vocabulary::LAST_LOGIN_FILTERS. Unlike the
// role and the status, no table column carries them, so the filter is the only
// place they appear.
export const userLastLoginFilters = {
  OVER_1_MONTH: 'over_1_month',
  OVER_1_YEAR: 'over_1_year',
  OVER_2_YEARS: 'over_2_years',
  NEVER: 'never',
} as const

export type UserLastLoginFilter =
  typeof userLastLoginFilters[keyof typeof userLastLoginFilters]

export const userAccesses = {
  ALL_FILES: 'all_files',
  LIMITED: 'limited',
} as const

export type UserAccess = typeof userAccesses[keyof typeof userAccesses]

export const userColumns = {
  NAME: 'name',
  STATUS: 'status',
  ROLE: 'role',
  ACCESS: 'access',
  FILES: 'files',
  LAST_LOGIN: 'last_login',
  ACTIONS: 'actions',
} as const

export type UserColumn = typeof userColumns[keyof typeof userColumns]

// The files count is not one of these. UserRow counts the files of a page after
// the list query has run, so the server has no column to order the firm by, and
// whether the column needs a sort at all is open in #34115.
export const sortableUserColumns = [
  userColumns.NAME,
  userColumns.LAST_LOGIN,
] as const

export type SortableUserColumn = typeof sortableUserColumns[number]

export const userSortDirections = {
  ASC: 'asc',
  DESC: 'desc',
} as const

export type UserSortDirection =
  typeof userSortDirections[keyof typeof userSortDirections]

export interface FilterOption {
  value: string
  labelKey: string
}

export interface UserRow {
  id: number
  name: string
  email: string
  status: UserStatus
  role: UserRole
  access: UserAccess
  filesCount: number
  lastLogin: string | null
  certified: boolean
}

export interface PaginationMeta {
  currentPage: number
  totalPages: number
  totalCount: number
}

export interface UsersListResponse {
  users: UserRow[]
  pagination: PaginationMeta
}

export const footerMailActions = {
  WELCOME_MAIL: 'welcome_mail',
  PASSWORD_RESET: 'password_reset',
} as const

export type FooterMailAction =
  typeof footerMailActions[keyof typeof footerMailActions]

export const twoFactorStatuses = {
  DISABLED: 'disabled',
  PENDING: 'pending',
  SKIPPED: 'skipped',
  ENABLED: 'enabled',
  RESET: 'reset',
} as const

export type TwoFactorStatus =
  typeof twoFactorStatuses[keyof typeof twoFactorStatuses]

export interface UserPanelUser {
  id: number | null
  name: string | null
  email: string | null
  locale: string | null
  isActive: boolean
  confirmed: boolean
  external: boolean
  admin: boolean
  staffAccess: boolean
  companyCreationAccess: boolean
  role: UserRole
}

export interface LocaleOption {
  value: string
  label: string
}

export interface UserPanelCapabilities {
  emailEditable: boolean
  vismaConnectFirm: boolean
  localeOptions: LocaleOption[]
  staffAccessEditable: boolean
  lastAdminInFirm: boolean
  companyCreationAvailable: boolean
  groupsSupported: boolean
  ssoSupported: boolean
  ssoLockedByVismaConnect: boolean
  firmFilesCount: number
  canResetTwoFactor: boolean
  twoFactorVisible: boolean
  footerMailAction: FooterMailAction
  deactivateBlockedReason: string | null
}

export interface SsoEntityOption {
  id: number
  label: string
}

export interface UserPanelAuthentication {
  twoFactorStatus: TwoFactorStatus
  twoFactorEnabled: boolean
  twoFactorUpdatedAt: string | null
  ssoEnabled: boolean
  ssoEntityId: number | null
  ssoEntities: SsoEntityOption[]
}

export interface UserPanelRole {
  id: number
  companyId: number
  companyName: string
}

export interface UserPanelGroup {
  id: number
  name: string
  usersCount: number
  removable: boolean
}

export interface UserPanelAccess {
  staffAccess: boolean
  firmRoles: UserPanelRole[]
  externalRoles: UserPanelRole[]
  customerRoles: UserPanelRole[]
  groups: UserPanelGroup[]
}

export interface UserPanelResponse {
  user: UserPanelUser
  capabilities: UserPanelCapabilities
  authentication: UserPanelAuthentication
  access: UserPanelAccess
  warning?: string | null
}

export const roleKinds = {
  FIRM: 'firm',
  CUSTOMER: 'customer',
  EXTERNAL: 'external',
} as const

export type RoleKind = typeof roleKinds[keyof typeof roleKinds]

export interface RoleWrite {
  id?: number
  companyId?: number
  role?: RoleKind
  _destroy?: typeof DESTROY
}

export interface GroupWrite {
  id: number
  _destroy?: typeof DESTROY
}

export interface GlobalUserWrite {
  email?: string
  enableSso?: boolean
  firmAccessSsoEntityId?: number | null
}

export interface UserPanelWrite {
  name?: string | null
  locale?: string | null
  admin?: boolean
  staffAccess?: boolean
  external?: boolean
  companyCreationAccess?: boolean
  sendWelcomeMail?: string | null
  globalUserAttributes?: GlobalUserWrite
  roles?: RoleWrite[]
  groups?: GroupWrite[]
}

// Every footer action answers `{message:}` on success and rejects otherwise
// (`users_controller.rb` `respond_with_flashed_action`), so a resolved response
// is the success, not a flag inside it.
export interface UserPanelActionResult {
  message: string
}

export interface AutocompleteOption {
  id: number
  name: string
}

// A row of the panel's three access lists, flattened from a role or a group so
// that one list component serves both shapes.
export interface AccessListItem {
  key: number | string
  title: string
  subtitle?: string
  removable: boolean
}

export type AccessListIcon = 'building' | 'users' | 'heart-pulse'

// A list without it has no Add button: an external contributor's files are
// granted from the client file itself.
export interface AccessListAdding {
  label: string
  placeholder: string
  search: (
    query: string,
    signal: AbortSignal
  ) => Promise<AutocompleteOption[] | null>
}

export const groupColumns = {
  NAME: 'name',
  FILES: 'files',
  MEMBERS: 'members',
  ACTIONS: 'actions',
} as const

export type GroupColumn = typeof groupColumns[keyof typeof groupColumns]

export const sortableGroupColumns = [
  groupColumns.NAME,
  groupColumns.FILES,
  groupColumns.MEMBERS,
] as const

export type SortableGroupColumn = typeof sortableGroupColumns[number]

export const groupSortDirections = {
  ASC: 'asc',
  DESC: 'desc',
} as const

export type GroupSortDirection =
  typeof groupSortDirections[keyof typeof groupSortDirections]

// Both counts are null on a group the viewer is not a member of, which is also
// what `allowedActions.edit` reports.
export interface GroupListRow {
  id: number
  name: string
  filesCount: number | null
  membersCount: number | null
  allowedActions: { edit: boolean }
}

export interface GroupsListResponse {
  groups: GroupListRow[]
  pagination: PaginationMeta
}

export interface GroupMember {
  id: number
  name: string
  email: string
}

export interface GroupFile {
  id: number
  name: string
}

export interface GroupDetailsResponse {
  id: number
  name: string
  members: GroupMember[]
  files: GroupFile[]
  allowedActions: { edit: boolean; destroy: boolean }
}

export interface GroupPanelWrite {
  name: string
  userIds: number[]
  companyIds: number[]
}

// What the group panel's picker needs of a staff user or a client file.
export interface GroupPickerItem {
  id: number
  name: string
}

export type GroupListKind = 'member' | 'file'

export interface ValidationErrorBody {
  errors: Record<string, string[]>
}
